import { useSnackbarController } from "@firecms/core";
import { Button } from "@firecms/ui";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useState } from "react";

import {
  AdminModal,
  adminModalInputClass,
  adminModalLabelClass,
} from "../custom/AdminModal";
import type { AdminTranslateFn } from "../i18n/getAdminT";
import type { Locale } from "../localization/constants";
import type { EventCollectionId } from "../workspace/collectionIds";
import type { EventUnitRelease } from "../hooks/useEventUnitReleases";

type ReleaseEventUnitResult = {
  success: boolean;
  skipped?: boolean;
  alreadyUnlocked?: boolean;
  unlocked?: boolean;
  scheduled?: boolean;
};

const pad = (value: number) => String(value).padStart(2, "0");

const toDatetimeLocalValue = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const formatUnlockAt = (date: Date, locale: Locale): string =>
  date.toLocaleString(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  });

async function callReleaseEventUnit(params: {
  accountId: string;
  eventId: string;
  unitId: string;
  unlockAt?: string;
}): Promise<ReleaseEventUnitResult> {
  const callable = httpsCallable<
    {
      accountId: string;
      eventId: string;
      unitId: string;
      unlockAt?: string;
    },
    ReleaseEventUnitResult
  >(getFunctions(getApp()), "releaseEventUnit");
  const result = await callable(params);
  return result.data;
}

export function UnitUnlockControls({
  eventId,
  unitId,
  release,
  locale,
  t,
}: {
  eventId: EventCollectionId;
  unitId: string;
  release: EventUnitRelease | undefined;
  locale: Locale;
  t: AdminTranslateFn;
}) {
  const snackbarController = useSnackbarController();
  const [busy, setBusy] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleValue, setScheduleValue] = useState(() =>
    toDatetimeLocalValue(new Date(Date.now() + 60 * 60 * 1000))
  );

  const unlocked = release?.unlocked === true;
  const scheduledAt =
    !unlocked && release?.unlockAt ? release.unlockAt : undefined;

  const runRelease = async (unlockAt?: string) => {
    setBusy(true);
    try {
      await callReleaseEventUnit({
        accountId: eventId.accountId,
        eventId: eventId.eventId,
        unitId,
        unlockAt,
      });
      setScheduleOpen(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("eventContent.unlockFailed");
      snackbarController.open({
        type: "error",
        message,
      });
    } finally {
      setBusy(false);
    }
  };

  const confirmSchedule = () => {
    const parsed = new Date(scheduleValue);
    if (Number.isNaN(parsed.getTime()) || parsed.getTime() <= Date.now()) {
      snackbarController.open({
        type: "warning",
        message: t("eventContent.scheduleInvalid"),
      });
      return;
    }
    void runRelease(parsed.toISOString());
  };

  if (unlocked) {
    return (
      <Button size="small" disabled>
        {t("eventContent.unlocked")}
      </Button>
    );
  }

  return (
    <>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <div className="flex flex-wrap justify-end gap-1">
          <Button
            size="small"
            disabled={busy}
            onClick={() => void runRelease()}
          >
            {t("eventContent.unlock")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => setScheduleOpen(true)}
          >
            {t("eventContent.scheduleUnlock")}
          </Button>
        </div>
        {scheduledAt ? (
          <span className="max-w-[12rem] text-right text-xs text-surface-500 dark:text-surface-400">
            {t("eventContent.scheduledFor", {
              when: formatUnlockAt(scheduledAt, locale),
            })}
          </span>
        ) : null}
      </div>

      <AdminModal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        ariaLabel={t("eventContent.scheduleTitle")}
        title={t("eventContent.scheduleTitle")}
        description={t("eventContent.scheduleDescription")}
        footer={
          <>
            <Button
              variant="text"
              onClick={() => setScheduleOpen(false)}
              disabled={busy}
            >
              {t("eventContent.close")}
            </Button>
            <Button color="primary" onClick={confirmSchedule} disabled={busy}>
              {t("eventContent.scheduleConfirm")}
            </Button>
          </>
        }
      >
        <label className={adminModalLabelClass} htmlFor={`unlock-at-${unitId}`}>
          {t("eventContent.scheduleUnlock")}
        </label>
        <input
          id={`unlock-at-${unitId}`}
          className={adminModalInputClass}
          type="datetime-local"
          min={toDatetimeLocalValue(new Date())}
          value={scheduleValue}
          onChange={(event) => setScheduleValue(event.target.value)}
        />
      </AdminModal>
    </>
  );
}
